import { useEffect, useRef, useState } from 'react';
import { i18n } from '@lingui/core';
import { Box, Button, makeStyles } from '@material-ui/core';
import { NavigateBefore } from '@material-ui/icons';
import { pick } from 'lodash';
import clsx from 'clsx';

import { Menu } from '../menu';
import Link, { buildLinkUrl } from '../../links/Link';
import { sideNavWidth } from '../SideNavStyles';
import { useMenus, useSetMenuContainer } from './MenuContextProvider';
import { useFilterState } from '../../../stores/FilterStateStore';

const useStyles = makeStyles((theme) => ({
    fullHeight: {
        ...theme.mixins.flex.displayFlex,
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.grow(1),

        width: '100%',
        maxWidth: '100%',
    },

    goBackButton: {
        padding: theme.spacing(1.5, 1),

        width: '100%',

        '& .MuiButton-label': {
            ...theme.mixins.flex.justifyContentFlexStart,
        },
    },

    menuTitle: {
        color: theme.palette.text.secondary,
        fontSize: '1.1rem',
        fontWeight: theme.typography.fontWeightMedium,
        cursor: 'default',
    },

    transitionerParent: {
        position: 'relative',
        overflow: 'hidden',
    },
}));

const DESCEND = 1;
const ASCEND = -1;

/**
 * This component is used to house the NavigationMenu menus in the application. Menus are rendered via a
 * createPortal call so they can live in the area a developer would expect them to in the component tree.
 * This container just creates an empty <div> for them, and saves the reference for when the menus actually
 * get rendered.
 */
export default function NavigationMenuContainer() {
    const [filterState] = useFilterState();
    const classes = useStyles();
    const menus = useMenus();

    const lastMenusLength = useRef(-1);
    const shiftDirection = useRef(DESCEND);

    // if don't have any menus, don't render anything
    if (!menus.length) {
        lastMenusLength.current = -1;
        return null;
    }

    if (lastMenusLength.current === -1) {
        lastMenusLength.current = menus.length;
    }

    // what "direction" have we moved. If we went back we ascended the stack,
    // if we went main -> admin, we've descended into another menu.
    if (lastMenusLength.current !== menus.length) {
        shiftDirection.current = menus.length > lastMenusLength.current ? DESCEND : ASCEND;
        lastMenusLength.current = menus.length;
    }

    // Note: direction is direction of motion. "left" comes in
    // from right to left, leaves left to right
    const activeMenuId = menus[0]?.id;
    const activeMenuDirection = shiftDirection.current === ASCEND ? 'right' : 'left';
    const inactiveMenuDirection = activeMenuDirection === 'left' ? 'right' : 'left';

    return (
        <div className={clsx(classes.fullHeight, classes.transitionerParent)}>
            {mapMenusWithParents(menus).map(({ menu, prev }) => {
                const isActive = activeMenuId === menu.id;

                let backUrl = '';
                if (prev?.lastUrl) {
                    backUrl = buildLinkUrl({ target: prev.lastUrl }, null, {
                        encoded: pick(filterState?.encoded || {}, 'TimeRange'),
                    });
                }

                return (
                    <MenuTransitioner
                        direction={isActive ? activeMenuDirection : inactiveMenuDirection}
                        isActive={isActive}
                        key={menu.id}
                    >
                        <Menu className={classes.fullHeight}>
                            {!!prev && !!prev.lastUrl && menu.showBack && (
                                <Button
                                    component={Link}
                                    to={backUrl}
                                    startIcon={<NavigateBefore />}
                                    className={classes.goBackButton}
                                >
                                    {prev.title}
                                </Button>
                            )}

                            {menu.showTitle && !!menu.title && (
                                <Box m={1} className={classes.menuTitle}>
                                    {i18n._(menu.title)}
                                </Box>
                            )}
                            <MenuHolder menu={menu} />
                        </Menu>
                    </MenuTransitioner>
                );
            })}
        </div>
    );
}

function MenuHolder(props) {
    const {
        menu: { id },
    } = props;

    const classes = useStyles();
    const refSetter = useSetMenuContainer(id);

    return <div ref={refSetter} id={id} className={classes.fullHeight} />;
}

function mapMenusWithParents(menus) {
    return menus.map((menu, index) => ({
        menu,
        prev: menus[index + 1],
    }));
}

const useTransitionStyles = makeStyles({
    root: {
        position: 'absolute',
        overflowX: 'hidden',
        overflowY: 'auto',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
    },

    slide: {
        transition: `transform 0.2s linear`,
    },
});

function MenuTransitioner(props) {
    const { children, isActive, direction } = props;
    const classes = useTransitionStyles();
    const lastActive = useRef(false);

    const [left, setLeft] = useState(getLeftBounds(direction));
    const [visible, setVisible] = useState(isActive);

    useEffect(() => {
        if (lastActive.current === isActive) return;
        lastActive.current = isActive;

        if (isActive) setVisible(true);

        setTimeout(() => setLeft(isActive ? 0 : getLeftBounds(direction)), 10);
    }, [isActive, direction]);

    return (
        <div
            className={clsx(classes.root, classes.slide)}
            onTransitionEnd={() => setVisible(isActive)}
            style={{ transform: `translateX(${left}px)` }}
        >
            {visible ? children : null}
        </div>
    );
}

function getLeftBounds(direction) {
    return direction === 'left' ? sideNavWidth : -sideNavWidth;
}
